import React from 'react';
import Toggle from 'material-ui/Toggle';
import Divider from 'material-ui/Divider';
import SearchFieldsActions from 'actions/search/SearchFieldsActions';
import ConstValues from 'utils/const';
import IconButtonGroup from 'components/pure/search/IconButtonGroup';
import CloseIcon from 'material-ui/svg-icons/navigation/close';
import CheckIcon from 'material-ui/svg-icons/navigation/check';
import {List} from 'immutable';
import classnames from 'classnames';
import './KeywordTopicSearchFieldStyle.styl';
import TopicSelect from 'components/pure/search/keyword-topic/TopicSelect';
import KeywordSelect from 'components/pure/search/keyword-topic/KeywordSelect';
import SvgIcon from 'components/pure/utils/icon/common/SvgIcon';
import {ReactComponent as KeywordIcon} from 'images/search/topic-keywords/keyword.svg';
import {ReactComponent as TopicIcon} from 'images/search/topic-keywords/topic.svg';
import {ReactComponent as KeywordStrikethroughIcon} from 'images/search/topic-keywords/keyword-strikethrough.svg';
import {ReactComponent as TopicStrikethroughIcon} from 'images/search/topic-keywords/topic-strikethrough.svg';
import {ReactComponent as RepIcon} from 'images/search/topic-keywords/rep.svg';
import {ReactComponent as ContactIcon} from 'images/search/topic-keywords/contact.svg';
import {ReactComponent as AnybodyIcon} from 'images/search/topic-keywords/anybody.svg';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import isEqual from 'lodash/isEqual';
import {ALL_OF_VALUE} from 'utils/const';
import {TOOLTIP_DELAY} from 'components/StatsPage2.utils';
import cn from 'classnames';
import {getSearchFieldClass} from 'components/stateful/search/getSearchFieldClass';

const SOME_OF_VALUE = 'some_of';
const TOPIC_SEARCH = 'topic_search';
const KEYWORD_SEARCH = 'keyword_search';

const SpeakerType = Object.freeze({
  EXTERNAL: 'contact',
  INTERNAL: 'rep',
  EITHER: 'anyone',
});

const TYPE_OPTIONS = List([
  {
    className: 'combobox-search-field-keyword-search-button',
    icon: KeywordIcon,
    strikethroughIcon: KeywordStrikethroughIcon,
    key: KEYWORD_SEARCH,
  },
  {
    className: 'combobox-search-field-topic-search-button',
    icon: TopicIcon,
    strikethroughIcon: TopicStrikethroughIcon,
    key: TOPIC_SEARCH,
  },
]);

const TYPE_LABELS = List([
  {name: 'Keyword', key: KEYWORD_SEARCH},
  {name: 'Topic', key: TOPIC_SEARCH},
]);

const MENTIONED_OPTIONS = List([
  {
    className: 'combobox-search-field-mentioned-search-button',
    icon: <CheckIcon />,
    key: true,
  },
  {
    className: 'combobox-search-field-not-mentioned-search-button',
    icon: <CloseIcon />,
    key: false,
  },
]);

const KEYWORD_MENTIONED_LABELS = List([
  {name: 'Mentioned', key: true},
  {name: 'Not Mentioned', key: false},
]);

/** Same as KEYWORD_MENTIONED_LABELS, but with different names. */
const TOPIC_MENTIONED_LABELS = List([
  {name: 'Discussed', key: true},
  {name: 'Not Discussed', key: false},
]);

const MENTIONED_BY_OPTIONS = List([
  {
    className: 'combobox-search-field-mentionedby-contact-search-button',
    icon: ContactIcon,
    key: SpeakerType.EXTERNAL,
  },
  {
    className: 'combobox-search-field-not-mentionedby-rep-search-button',
    icon: RepIcon,
    key: SpeakerType.INTERNAL,
  },
  {
    className: 'combobox-search-field-not-mentionedby-any-search-button',
    icon: AnybodyIcon,
    key: SpeakerType.EITHER,
  },
]);

const MENTIONED_BY_LABELS = List([
  {name: 'External', key: SpeakerType.EXTERNAL},
  {name: 'Internal', key: SpeakerType.INTERNAL},
  {name: 'Either', key: SpeakerType.EITHER},
]);

const lineItemIconColor = '#91A1AB';
const lineItemCloseColor = '#BDBDBD';

export default class KeywordTopicSearchField extends React.Component {
  constructor(props) {
    super(props);

    this.onToggleSearchField = this.onToggleSearchField.bind(this);
    this.addLineItem = this.addLineItem.bind(this);
    this.removeLineItem = this.removeLineItem.bind(this);
    this.makeLineItemTooltip = this.makeLineItemTooltip.bind(this);
    this.reset = this.reset.bind(this);

    this.state = this.initialState;
  }

  initialState = {
    // content type: keyword or topic?
    currentSelectedContentTypeKey: KEYWORD_SEARCH,
    // mentioned or didn't mention? we allow for negation
    currentSelectedMentionedKey: true,
    // current mentioned by - either/caller/contact
    currentSelectedMentionedByKey: SpeakerType.EITHER,
  };

  /**
   * Add a content line item to what is currently stored in state.
   *
   * Value is either the topic id or the keyword text, depending.
   *
   * @param idOrValue
   * @param isTopic - keyword or topic
   */
  addLineItem(idOrValue, isTopic) {
    const {currentSelectedMentionedKey, currentSelectedMentionedByKey} = this.state;
    const {contentLineItems, allOrAny} = this.props;

    if (!idOrValue || idOrValue === '') return;

    const topicLineItem = {
      value: idOrValue,
      isTopic,
      mentioned: currentSelectedMentionedKey,
      by: currentSelectedMentionedByKey,
    };

    // cant add the same value (shouldnt be able to via topic, but prevent here by keyword too)
    if (contentLineItems.some(li => isEqual(li, topicLineItem))) return;

    this.doSearch(contentLineItems.push(topicLineItem), allOrAny);
  }

  reset() {
    this.setState(this.initialState);
    SearchFieldsActions.removeSearchField(this.props.searchField);
    SearchFieldsActions.clearKeywordTopic();
  }

  /**
   * Given the line item, remove from state and update
   *
   * @param lineItem
   */
  removeLineItem(lineItem) {
    const {contentLineItems, allOrAny} = this.props;

    // if remove is called and we only had one, just reset the component
    if (contentLineItems.size === 1) {
      this.reset();
    } else {
      this.doSearch(
        contentLineItems.filterNot(c => isEqual(c, lineItem)),
        allOrAny
      );
    }
  }

  onToggleSearchField(_, toggled) {
    SearchFieldsActions.updateSearchWidgetToggleState(
      ConstValues.keywordTopicWidgetType,
      toggled,
      this.props.searchField
    );

    // if we turned it off, reset state
    if (!toggled) {
      this.reset();
    }
  }

  doSearch(contentLineItems, allOrAny) {
    // note: this updates the widget data up in the search fields store
    SearchFieldsActions.updateKeywordTopic(this.props.searchField, {
      conditions: contentLineItems,
      type: allOrAny,
    });
  }

  makeLineItemTooltip({value, isTopic, mentioned, by}) {
    const {topics} = this.props;

    const what = !isTopic ? value : topics.find(t => t.id === value)?.word;
    const who =
      by === SpeakerType.EITHER
        ? 'Anyone'
        : by === SpeakerType.INTERNAL
        ? 'Internal speakers'
        : 'External speakers';
    const discussed = isTopic
      ? mentioned
        ? 'discussed'
        : 'did not discuss'
      : mentioned
      ? 'mentioned'
      : 'did not mention';

    return <p>{`${who} ${discussed} ${what}`}</p>;
  }

  render() {
    const {
      toggled,
      canToggleKeywordTopicField,
      searchField,
      topics,
      contentLineItems = List(),
      allOrAny,
    } = this.props;
    const {
      currentSelectedContentTypeKey,
      currentSearchTerm,
      currentSelectedMentionedKey,
      currentSelectedMentionedByKey,
    } = this.state;

    const showSearchWidget = toggled && canToggleKeywordTopicField;
    const mentionedLabels =
      currentSelectedContentTypeKey === TOPIC_SEARCH
        ? TOPIC_MENTIONED_LABELS
        : KEYWORD_MENTIONED_LABELS;

    const topicLineItems = contentLineItems.filter(li => li.isTopic);
    // allow selections for line items we havent seen (needs to consider mentioned values)
    const selectableTopics = topics.filter(
      t =>
        !topicLineItems.some(
          li =>
            li.value === t.id &&
            currentSelectedMentionedKey === li.mentioned &&
            currentSelectedMentionedByKey === li.by
        )
    );

    const classes = cn('keyword-topic-search-widget', getSearchFieldClass(searchField.name));
    return (
      <div className={classes}>
        <Toggle
          label={searchField.name}
          defaultToggled={false}
          onToggle={this.onToggleSearchField}
          toggled={showSearchWidget}
        />
        <Divider className="combobox-divider" />
        {showSearchWidget && (
          <div>
            <div className={'two-choice-chooser'}>
              <IconButtonGroup
                buttonOptions={TYPE_OPTIONS.map(o => ({
                  ...o,
                  icon: <SvgIcon icon={o.icon} />,
                }))}
                buttonLabels={TYPE_LABELS}
                selectedOptionKey={currentSelectedContentTypeKey}
                highlightMode="foreground"
                onTouchTapIconButton={currentSelectedContentTypeKey =>
                  this.setState({currentSelectedContentTypeKey})
                }
                showAllLabels
              />
            </div>
            <div className={'two-choice-chooser'}>
              <IconButtonGroup
                buttonOptions={MENTIONED_OPTIONS}
                buttonLabels={mentionedLabels}
                selectedOptionKey={currentSelectedMentionedKey}
                highlightMode="foreground"
                onTouchTapIconButton={key =>
                  this.setState({
                    currentSelectedMentionedKey: key === 'true',
                  })
                }
                showAllLabels
              />
            </div>
            <div className="by-wrapper">
              <div>
                <span>by</span>
              </div>
            </div>
            <div className="three-choice-chooser">
              <IconButtonGroup
                buttonOptions={MENTIONED_BY_OPTIONS.map(o => ({
                  ...o,
                  icon: <o.icon />,
                }))}
                buttonLabels={MENTIONED_BY_LABELS}
                selectedOptionKey={currentSelectedMentionedByKey}
                highlightMode="foreground"
                onTouchTapIconButton={currentSelectedMentionedByKey =>
                  this.setState({
                    currentSelectedMentionedByKey,
                  })
                }
                showAllLabels
                fontSize={14}
              />
            </div>
            {currentSelectedContentTypeKey === TOPIC_SEARCH && (
              <TopicSelect
                topics={selectableTopics}
                currentSearchTerm={currentSearchTerm}
                removeKeywordTopicComboboxItem={this.removeLineItem}
                handleAddTopic={tid => this.addLineItem(tid, true)}
              />
            )}
            {currentSelectedContentTypeKey === KEYWORD_SEARCH && (
              <KeywordSelect
                handleAddKeyword={keyword => this.addLineItem(keyword.trim(), false)}
              />
            )}
            {contentLineItems && (
              <div className="line-items">
                {contentLineItems.map((c, i) => (
                  <div key={i} className="line-item">
                    <span>{c.isTopic ? topics.find(t => t.id === c.value)?.word : c.value}</span>
                    <GenericTooltip
                      tooltip={this.makeLineItemTooltip(c)}
                      delay={TOOLTIP_DELAY}
                      positionTop>
                      <div className="buttons">
                        <SvgIcon
                          icon={MENTIONED_BY_OPTIONS.find(o => o.key === c.by).icon}
                          color={lineItemIconColor}
                          width={18}
                          height={18}
                        />
                        {c.mentioned && (
                          <SvgIcon
                            icon={c.isTopic ? TopicIcon : KeywordIcon}
                            color={lineItemIconColor}
                            width={18}
                            height={18}
                            className={'type-icon'}
                          />
                        )}
                        {!c.mentioned && (
                          <SvgIcon
                            icon={c.isTopic ? TopicStrikethroughIcon : KeywordStrikethroughIcon}
                            color={lineItemIconColor}
                            width={18}
                            height={18}
                            className={'type-icon'}
                          />
                        )}
                        <CloseIcon
                          className="remove-line-item"
                          color={lineItemCloseColor}
                          onClick={() => this.removeLineItem(c)}
                        />
                      </div>
                    </GenericTooltip>
                  </div>
                ))}
              </div>
            )}
            <div className={classnames('icon-button-group-parent', 'boolean-switch')}>
              <div
                className={classnames(
                  'keyword-topic-search-icon-button',
                  allOrAny === ALL_OF_VALUE ? 'active' : null
                )}
                onClick={() => this.doSearch(contentLineItems, ALL_OF_VALUE)}>
                All
              </div>
              <div
                className={classnames(
                  'keyword-topic-search-icon-button',
                  allOrAny === SOME_OF_VALUE ? 'active' : null
                )}
                onClick={() => this.doSearch(contentLineItems, SOME_OF_VALUE)}>
                Any
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}
